import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router'
import { CalendarTab } from '@/components/property/calendar-tab'
import {
  DayEventBlocks,
  DepositLegend,
  ReviewLegend,
  SupplyLegend,
} from '@/components/calendar/event-blocks'
import { LegendSwatch } from '@/components/calendar/legend'
import {
  depositEventBlocks,
  reviewEventBlocks,
  supplyEventBlocks,
} from '@/components/calendar/event-block-specs'
import { ErrorState } from '@/components/ui/error-state'
import {
  BLOCK_BAR_CLASSES,
  HOLD_BAR_CLASSES,
  blockKey,
  buildBlockBars,
} from '@/components/calendar/block-bars'
import { GAP_BAR_CLASSES, buildGapBars } from '@/components/calendar/gap-bars'
import {
  CleaningLegend,
  buildCleaningBar,
  isCleaningDrawn,
} from '@/components/calendar/cleaning-bars'
import { localDate } from '@/components/cleaning/presentation'
import { type CalendarReservation, type MonthBar } from '@/components/calendar/month-grid'
import { api } from '@/api/client'
import { buildReviewsToLeave } from '@/lib/review-events'
import { supplyOpenValue } from '@/lib/supply-events'
import { gapOpenValue, parseGapOpen } from '@/lib/gap-events'
import { usePropertyCalendar } from '@/hooks/use-property-calendar'
import { usePropertyBlocks } from '@/hooks/use-property-blocks'
import { usePropertyPricingCalendar } from '@/hooks/useApi'
import { SM_UP_QUERY, useMediaQuery } from '@/hooks/use-media-query'
import { currentMonthParam, isMonthParam, monthParamToDate } from '@/lib/calendar-month'
import { addDays, format } from 'date-fns'
import { AgendaView } from './agenda-view'
import { buildAgenda, monthDays } from './agenda'
import { LayerToggles } from './layer-toggles'
import { reviewsToLeaveGroups } from './reviews-to-leave'
import {
  REVIEWS_TO_LEAVE_OPEN,
  ReviewsToLeaveSheet,
  type ReviewsToLeaveSheetState,
} from './reviews-to-leave-sheet'
import { CALENDAR_LAYERS } from './layers'
import { loadingBands } from './loading-bands'
import { deriveReadiness } from './readiness'
import { newBlockOpenValue, parseBlockOpen } from './block-utils'
import { dayOpenValue } from './day-list'
import { DEPOSIT_SECTION, REVIEW_SECTION } from './stay-sheet'
import { CalendarSheets } from './calendar-sheets'
import { buildDayGroups } from './day-groups'
import {
  cleaningOpenValue,
  openBlockSheet,
  parseCleaningOpen,
  parseStayOpen,
  stayOpenValue,
  type CalendarSheetNav,
} from './sheet-open'
import { monthRange, useCalendarData } from './use-calendar-data'
import { useLayerPrefs } from './use-layer-prefs'

interface PropertyCalendarTabProps {
  propertyId: string
  /** Names the cleaning sheet, whose body is the cleaning board's. */
  propertyName: string
  isActive: boolean
}

// What `?open=` can hold: `stay:<reservation id>`, `cleaning:<job id>`,
// `supply:<category>:<YYYY-MM-DD>`, `gap:<first night>`, `day:<YYYY-MM-DD>`,
// a block's, or the reviews-to-leave list (see `CalendarSheets`).

/**
 * Week-row floor for this tab: day number and price, band 1 (stays, blocks),
 * band 2 (cleanings) and room for two event blocks. Taller than the grid's
 * default so the bands have room and every quiet row of a month is the same
 * height; a week with a busier day grows to fit it (see `DayEventBlocks`).
 */
const DAY_MIN_HEIGHT = 120

// `?section=deposits` or `?section=reviews` rides along with `open=stay:<id>` and
// says where in the sheet to scroll to. It means nothing without a stay open.
const SECTION_PARAM = 'section'
/** The day list a block's sheet was opened from (`YYYY-MM-DD`), so the sheet can go back to it. */
const FROM_DAY_PARAM = 'from'

/**
 * The property page's Calendar tab: one aggregated read per visible month,
 * drawn on the shared `CalendarTab` grid, with the sheet for whatever is
 * opened.
 *
 * The URL holds the state worth sharing - `?tab=calendar&month=2026-10&open=stay:12196955`,
 * `open=cleaning:<job id>`, `open=supply:batteries:2026-10-13` or
 * `open=gap:2026-10-14` - so a link to a booking, a cleaning, a supply marker or
 * an orphan gap on the calendar can be pasted into a ticket. Which
 * layers are switched on is a per-viewer convenience and lives in
 * localStorage (see `useLayerPrefs`).
 *
 * Nothing on the grid writes: it shows what the API returned. The stay sheet's
 * actions are links to places that already exist; the cleaning sheet carries
 * the cleaning board's own actions, and each one that lands refetches the month.
 */
export function PropertyCalendarTab({
  propertyId,
  propertyName,
  isActive,
}: PropertyCalendarTabProps) {
  const [searchParams, setSearchParams] = useSearchParams()
  const rawMonth = searchParams.get('month')
  // A missing or malformed month opens on the current one.
  const month = isMonthParam(rawMonth) ? rawMonth : currentMonthParam()
  const rawOpen = searchParams.get('open')
  const openSection = searchParams.get(SECTION_PARAM)
  const openStayId = parseStayOpen(rawOpen)
  const openCleaningId = parseCleaningOpen(rawOpen)
  const openGapNight = parseGapOpen(rawOpen)
  const openBlock = parseBlockOpen(rawOpen)
  const openReviewsToLeave = rawOpen === REVIEWS_TO_LEAVE_OPEN

  // Under 640px (the breakpoint the sheet goes full screen at) the month is an
  // agenda, not a grid. Everything it reads from - the month, the open sheet,
  // the layers - is the URL's or the viewer's, so crossing the breakpoint
  // changes only how the same state is drawn.
  const isPhone = !useMediaQuery(SM_UP_QUERY)

  const calendarQuery = usePropertyCalendar(propertyId, month, { enabled: isActive })
  const calendar = calendarQuery.data
  const { isVisible, toggle } = useLayerPrefs()
  // The blocks in full: what the block panels show, and the blocks the grid
  // draws that the month read does not carry yet.
  const blocksQuery = usePropertyBlocks(propertyId, { enabled: isActive })

  const setParam = (key: string, value: string | null, { replace = false } = {}) => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev)
        if (value === null) next.delete(key)
        else next.set(key, value)
        return next
      },
      { replace }
    )
  }
  // The open sheet: `?open=`, with a section and the day list a block was
  // reached from riding along. Both belong to the sheet they were opened with.
  const nav: CalendarSheetNav = {
    open: rawOpen,
    section: openSection,
    fromDay: searchParams.get(FROM_DAY_PARAM),
    go: (value, { replace = false, fromDay, section } = {}) =>
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev)
          if (value === null) next.delete('open')
          else next.set('open', value)
          next.delete(SECTION_PARAM)
          next.delete(FROM_DAY_PARAM)
          if (fromDay) next.set(FROM_DAY_PARAM, fromDay)
          if (section) next.set(SECTION_PARAM, section)
          return next
        },
        { replace }
      ),
  }

  // The crews, for each bar's colour and name. Shared with the cleaning board
  // by key. They arrive on their own, after the month read or before it, so
  // until they do a bar that names a crew shows a placeholder where the name
  // goes (see `crewLoading`), never the id it carries.
  const teamsQuery = useQuery({
    queryKey: ['cleaning', 'teams'],
    queryFn: () => api.getCleaningTeams(),
    enabled: isActive,
  })
  const teams = useMemo(() => teamsQuery.data?.teams ?? [], [teamsQuery.data])

  // The grid's nightly prices: the same query, with the same key, that the grid
  // asks for itself (so no second request); it is read here only to know
  // whether they are still on their way. The phone's agenda has none.
  const monthDate = monthParamToDate(month)
  const pricesQuery = usePropertyPricingCalendar(isPhone ? null : propertyId, {
    year: monthDate.getFullYear(),
    month: monthDate.getMonth() + 1,
  })

  // Per layer: loading, ready, error or hidden - the one answer everything
  // below goes by. A layer is never decided about on its own here.
  const readiness = deriveReadiness({
    month,
    calendar,
    isPlaceholderData: calendarQuery.isPlaceholderData,
    monthQuery: calendarQuery,
    teamsQuery,
    pricesQuery: isPhone ? undefined : pricesQuery,
    isVisible,
  })
  const { summaries, stale } = readiness
  const crewLoading = readiness.crew === 'loading'

  const range = useMemo(() => monthRange(month), [month])
  const data = useCalendarData({ calendar, range, blocksData: blocksQuery.data, isVisible })
  const {
    stays,
    cleaningJobs,
    blockItems,
    reviewItems,
    gapItems,
    timeZone,
    markersByDay,
    supplyMarkersByDay,
    reviewMarkersByDay,
    today,
    guestNames,
  } = data
  const showStays = isVisible('stays')
  const showBlocks = isVisible('blocks')
  const showCleaning = isVisible('cleaning')
  const showDeposits = isVisible('deposits')
  const showSupplies = isVisible('supplies')
  const showReviews = isVisible('reviews')
  const showGaps = isVisible('orphan_gaps')

  // The grid reads reservations; the calendar's stays are already that shape.
  const reservations = useMemo<CalendarReservation[]>(
    () => (showStays ? stays : []),
    [stays, showStays]
  )
  const needsReplyIds = useMemo(
    () => new Set(stays.filter(s => s.needs_reply).map(s => s.reservation_id)),
    [stays]
  )
  // Not memoised, like the cleaning bars: each click handler closes over `nav`.
  const openBlockKey =
    openBlock && openBlock.kind !== 'new'
      ? blockKey({ kind: openBlock.kind === 'hold' ? 'uplisting' : 'owner', id: openBlock.id })
      : null
  const blockBars =
    showBlocks && calendar?.layers.blocks.status === 'ok'
      ? buildBlockBars(blockItems, {
          onOpen: block => openBlockSheet(nav.go, block),
          focusedKey: openBlockKey,
        })
      : undefined
  const ownerBlocksDrawn = blockItems.some(block => block.kind === 'owner')
  const holdsDrawn = blockItems.some(block => block.kind === 'uplisting')

  // Not memoised: each bar's click handler closes over `nav`, which is
  // new every render, and a month holds a few dozen cleanings at most.
  const cleaningBars: MonthBar[] = showCleaning
    ? cleaningJobs.filter(isCleaningDrawn).flatMap(job => {
        const day = job.local_date ?? localDate(job.scheduled_at, timeZone)
        if (!day) return []
        return [
          buildCleaningBar(job, {
            day,
            team: teams.find(team => team.id === job.team_id),
            crewLoading,
            focused: job.id === openCleaningId,
            testId: 'calendar-cleaning-bar',
            onClick: () => nav.go(cleaningOpenValue(job.id)),
          }),
        ]
      })
    : []
  const gapBars: MonthBar[] = showGaps
    ? buildGapBars(gapItems, {
        focusedFirstNight: openGapNight,
        stays: reservations,
        onOpen: gap => nav.go(gapOpenValue(gap.first_night)),
      })
    : []
  // A band whose layers are still being read shows a pulsing strip, not an
  // empty band that reads as "none" - also while the grid holds the last
  // month, whose bars are clipped to the month on screen and so are mostly not
  // there to be seen.
  const bandsLoading = !readiness.monthFailed
  const layerLoading = (id: keyof typeof readiness.layers) =>
    bandsLoading && readiness.layers[id].state === 'loading'
  const placeholderBars = loadingBands(month, {
    stays: layerLoading('stays') || layerLoading('blocks') || layerLoading('orphan_gaps'),
    cleaning: layerLoading('cleaning'),
  })
  const eventLayersLoading =
    layerLoading('deposits') || layerLoading('supplies') || layerLoading('reviews')
  const extraBars = [...placeholderBars, ...(blockBars ?? []), ...gapBars, ...cleaningBars]

  // "No reservations" is only true when every layer the grid is drawing was
  // read for this month: a layer that is loading, failed or still the last
  // month's is missing data, not an empty month.
  const canClaimEmpty =
    showStays &&
    readiness.enabledLayersReady &&
    markersByDay.size === 0 &&
    supplyMarkersByDay.size === 0 &&
    reviewMarkersByDay.size === 0

  // The month could not be read at all: say so with a way to retry, in place of
  // the grid. The title and month paging stay, so the operator can leave it.
  const monthError = readiness.monthFailed ? (
    <ErrorState
      error={calendarQuery.error as Error}
      title={`Could not load ${readiness.monthLabel}`}
      onRetry={() => void calendarQuery.refetch()}
      compact
    />
  ) : undefined

  // A block opens its stay's sheet scrolled to the part the event belongs to.
  const openDeposit = (reservationId: string) =>
    nav.go(stayOpenValue(reservationId), { section: DEPOSIT_SECTION })
  const openReview = (reservationId: string) =>
    nav.go(stayOpenValue(reservationId), { section: REVIEW_SECTION })

  // The reviews you still owe. Only a month read in full speaks: while the read
  // is on its way (or the grid still holds the last month's), failed, or the
  // layer is switched off, there is no list and no number - never "nothing owed".
  const reviewsReady = readiness.layers.reviews.state === 'ready'
  const reviewsToLeave = useMemo(
    () => (reviewsReady ? buildReviewsToLeave(reviewItems, today) : null),
    [reviewsReady, reviewItems, today]
  )
  const reviewsToLeaveChip =
    reviewsToLeave &&
    reviewsToLeave.owed.length + reviewsToLeave.thankYou.length + reviewsToLeave.untracked.length >
      0
      ? { owed: reviewsToLeave.owed.length }
      : null
  const reviewsToLeaveState: ReviewsToLeaveSheetState = (() => {
    if (readiness.monthLoading || stale) return { status: 'pending' }
    if (!isVisible('reviews')) {
      return {
        status: 'unavailable',
        message:
          'The Reviews layer is switched off. Switch it on above to see the reviews you owe.',
      }
    }
    const summary = summaries.reviews
    if (summary.status === 'error') {
      return {
        status: 'unavailable',
        message: `${summary.message} What you owe is not known until it can be.`,
      }
    }
    if (!reviewsToLeave) return { status: 'pending' }
    return {
      status: 'ok',
      owedCount: reviewsToLeave.owed.length,
      groups: reviewsToLeaveGroups(reviewsToLeave, { today, guestNames, onOpen: openReview }),
    }
  })()

  const openSupplyItem = (marker: { category: string; day: string }) =>
    nav.go(supplyOpenValue(marker.category, marker.day))

  const showMarkers = showDeposits || showSupplies || showReviews
  const openDayList = (day: string) => nav.go(dayOpenValue(day))

  // One day's events as blocks, deposits first, then supplies, then reviews.
  const eventBlocksFor = (day: string) => [
    ...depositEventBlocks(markersByDay.get(day) ?? [], timeZone, openDeposit),
    ...supplyEventBlocks(supplyMarkersByDay.get(day) ?? [], openSupplyItem),
    ...reviewEventBlocks(reviewMarkersByDay.get(day) ?? [], openReview),
  ]

  const dayGroupsInput = {
    data,
    range,
    isVisible,
    blocksRead: calendar?.layers.blocks.status === 'ok',
    blocksData: blocksQuery.data,
    teams,
    crewLoading,
    go: nav.go,
  }

  // The agenda says "nothing" only about layers it read completely. Whether it
  // may is decided here once, for the quiet runs and the empty month alike:
  // - every layer that is on came back ok for this month (not failed, not
  //   still the last month's data), and none of them carries a caveat (a stale
  //   or cut-short gap snapshot says some gaps may be missing);
  // - for a quiet run, also every layer is on: a switched-off layer may have
  //   something on those days. The empty month's own line can say "in the
  //   layers shown" instead.
  const enabledLayers = CALENDAR_LAYERS.filter(({ id }) => isVisible(id))
  const agendaLoading = readiness.monthLoading || stale
  const agendaRead =
    !agendaLoading &&
    enabledLayers.length > 0 &&
    enabledLayers.every(({ id }) => readiness.layers[id].state === 'ready')
  const agendaCaveated = enabledLayers.some(({ id }) => {
    const summary = summaries[id]
    return summary.status === 'ok' && !!summary.caveats?.length
  })
  const agendaComplete = agendaRead && !agendaCaveated
  const agendaEntries =
    isPhone && !agendaLoading
      ? buildAgenda(monthDays(month), day =>
          buildDayGroups(dayGroupsInput, day, true).flatMap(group => group.rows)
        )
      : []
  const agendaEmptyMessage =
    enabledLayers.length === 0
      ? 'Every layer is switched off. Switch one on above to see the month.'
      : !agendaRead
        ? 'Nothing to show. Some layers could not be read, see above.'
        : agendaCaveated
          ? 'Nothing found this month, but some data may be missing, see above.'
          : enabledLayers.length < CALENDAR_LAYERS.length
            ? 'Nothing scheduled this month in the layers shown.'
            : 'Nothing scheduled this month.'

  return (
    <>
      <CalendarTab
        propertyId={propertyId}
        reservations={reservations}
        needsReplyReservationIds={needsReplyIds}
        month={month}
        onMonthChange={next => setParam('month', next)}
        showToday
        extraBars={extraBars}
        extraLegend={
          <>
            {showBlocks && summaries.blocks.status === 'ok' && ownerBlocksDrawn && (
              <LegendSwatch className={BLOCK_BAR_CLASSES}>Owner block</LegendSwatch>
            )}
            {showBlocks && summaries.blocks.status === 'ok' && holdsDrawn && (
              <LegendSwatch className={HOLD_BAR_CLASSES}>Blocked in Uplisting</LegendSwatch>
            )}
            {showGaps && gapBars.length > 0 && (
              <LegendSwatch className={GAP_BAR_CLASSES}>Empty nights</LegendSwatch>
            )}
            {showDeposits && markersByDay.size > 0 && <DepositLegend />}
            {showSupplies && supplyMarkersByDay.size > 0 && (
              <SupplyLegend markers={[...supplyMarkersByDay.values()].flat()} />
            )}
            {showReviews && reviewMarkersByDay.size > 0 && (
              <ReviewLegend markers={[...reviewMarkersByDay.values()].flat()} />
            )}
            {showCleaning && (cleaningBars.length > 0 || layerLoading('cleaning')) && (
              <CleaningLegend />
            )}
          </>
        }
        controls={
          monthError ? undefined : (
            <LayerToggles
              readiness={readiness}
              isVisible={isVisible}
              onToggle={toggle}
              reviewsToLeave={reviewsToLeaveChip}
              onOpenReviewsToLeave={() => nav.go(REVIEWS_TO_LEAVE_OPEN)}
            />
          )
        }
        error={monthError}
        busy={readiness.monthLoading || stale || (crewLoading && showCleaning)}
        dayMinHeight={DAY_MIN_HEIGHT}
        renderDayFooter={
          eventLayersLoading
            ? () => <EventBlocksPlaceholder />
            : showMarkers
              ? day => {
                  const key = format(day, 'yyyy-MM-dd')
                  return (
                    <DayEventBlocks
                      blocks={eventBlocksFor(key)}
                      day={key}
                      onOpenDay={() => openDayList(key)}
                    />
                  )
                }
              : undefined
        }
        onSelectDay={day => openDayList(format(day, 'yyyy-MM-dd'))}
        // The last dragged day is the last blocked night; the block ends the day after.
        onDragSelect={(first, last) =>
          nav.go(
            newBlockOpenValue(format(first, 'yyyy-MM-dd'), format(addDays(last, 1), 'yyyy-MM-dd'))
          )
        }
        focusedReservationId={openStayId}
        agenda={
          isPhone ? (
            <AgendaView
              entries={agendaEntries}
              today={today}
              loading={agendaLoading}
              claimNothing={agendaComplete && enabledLayers.length === CALENDAR_LAYERS.length}
              emptyMessage={agendaEmptyMessage}
              onBlockDates={() =>
                nav.go(newBlockOpenValue(today?.startsWith(month) ? today : `${month}-01`))
              }
            />
          ) : undefined
        }
        canClaimEmpty={canClaimEmpty}
        isLoading={readiness.monthLoading}
        isStale={stale}
        onSelectReservation={reservation => {
          const id = String(reservation.reservation_id || reservation.id || '')
          if (id) nav.go(stayOpenValue(id))
        }}
      />

      {/* A month that could not be read has nothing for a sheet to be built from:
          the error above is the whole answer until Retry lands. */}
      {!readiness.monthFailed && (
        <>
          <CalendarSheets
            propertyId={propertyId}
            propertyName={propertyName}
            calendar={calendar}
            isPlaceholderData={calendarQuery.isPlaceholderData}
            readiness={readiness}
            data={data}
            range={range}
            isVisible={isVisible}
            teams={teams}
            crewLoading={crewLoading}
            blocksQuery={blocksQuery}
            nav={nav}
          />

          {openReviewsToLeave && (
            <ReviewsToLeaveSheet
              state={reviewsToLeaveState}
              onClose={() => nav.go(null, { replace: true })}
            />
          )}
        </>
      )}
    </>
  )
}

/**
 * Stands where a day's event blocks will go while their layers are being read: a
 * quiet pill, so a day cell does not read as "no events" before it knows.
 */
function EventBlocksPlaceholder() {
  return (
    <div
      aria-hidden="true"
      data-testid="calendar-loading-blocks"
      className="h-4 w-3/5 animate-pulse rounded border border-textFaint/30 bg-textFaint/20 motion-reduce:animate-none"
    />
  )
}
